/**
 * Resources Landing — Custom filter dropdowns
 *
 * Progressively-enhanced listbox pattern for the three filter controls in the
 * hero search form. Keyboard nav: ArrowDown/Up to move focus, Enter/Space to
 * select, Escape to close. Click-outside and scroll-clipping are also handled.
 */

const SELECTOR_WRAP    = '[data-filter-dropdown]';
const SELECTOR_TRIGGER = '[data-filter-trigger]';
const SELECTOR_PANEL   = '[data-filter-panel]';
const SELECTOR_OPTION  = '[data-filter-option]';
const SELECTOR_LABEL   = '[data-filter-label]';
const SELECTOR_INPUT   = '[data-filter-input]';

class FilterDropdown {
    /**
     * @param {HTMLElement} wrap – the `.resources-landing__filter-wrap` element
     */
    constructor(wrap) {
        this.wrap    = wrap;
        this.trigger = wrap.querySelector(SELECTOR_TRIGGER);
        this.panel   = wrap.querySelector(SELECTOR_PANEL);
        this.options = Array.from(wrap.querySelectorAll(SELECTOR_OPTION));
        this.label   = wrap.querySelector(SELECTOR_LABEL);
        this.input   = wrap.querySelector(SELECTOR_INPUT);

        if (!this.trigger || !this.panel || !this.options.length) {
            return;
        }

        this.focusedIndex = -1;
        this._bindEvents();
        this._alignPanel();
    }

    // ── Public ─────────────────────────────────────────────────────────────────

    open() {
        this.panel.hidden = false;
        this.trigger.setAttribute('aria-expanded', 'true');
        this._alignPanel();

        // Focus the currently-selected option (or the first one)
        const selectedIdx = this.options.findIndex(
            (o) => o.getAttribute('aria-selected') === 'true',
        );
        this._moveFocus(selectedIdx >= 0 ? selectedIdx : 0);
    }

    close() {
        this.panel.hidden = true;
        this.trigger.setAttribute('aria-expanded', 'false');
        this._clearFocus();
    }

    isOpen() {
        return !this.panel.hidden;
    }

    select(option) {
        const value = option.dataset.value ?? '';
        const text  = option.textContent.trim();

        // Update aria-selected on all options
        this.options.forEach((o) => o.setAttribute('aria-selected', 'false'));
        option.setAttribute('aria-selected', 'true');

        // Sync label and hidden input (input may not be present on all pages)
        this.label.textContent = text;
        if (this.input) {
            this.input.value = value;
        }

        // Mark trigger active when non-default
        this.trigger.classList.toggle('is-active', value !== '');

        this.close();
        this.trigger.focus();
    }

    // ── Private ────────────────────────────────────────────────────────────────

    _bindEvents() {
        this.trigger.addEventListener('click', () => {
            this.isOpen() ? this.close() : this.open();
        });

        this.trigger.addEventListener('keydown', (e) => {
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                e.preventDefault();
                this.open();
            }
        });

        this.panel.addEventListener('keydown', (e) => this._onPanelKeydown(e));

        this.options.forEach((option) => {
            option.addEventListener('click', () => this.select(option));
            option.addEventListener('mousemove', () => {
                const idx = this.options.indexOf(option);
                this._moveFocus(idx);
            });
        });
    }

    _onPanelKeydown(e) {
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                this._moveFocus(
                    Math.min(this.focusedIndex + 1, this.options.length - 1),
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                if (this.focusedIndex <= 0) {
                    this.close();
                    this.trigger.focus();
                } else {
                    this._moveFocus(this.focusedIndex - 1);
                }
                break;
            case 'Enter':
            case ' ':
                e.preventDefault();
                if (this.focusedIndex >= 0) {
                    this.select(this.options[this.focusedIndex]);
                }
                break;
            case 'Escape':
                e.preventDefault();
                this.close();
                this.trigger.focus();
                break;
            case 'Tab':
                this.close();
                break;
            default:
                // Jump to first option starting with pressed key
                if (e.key.length === 1) {
                    const char = e.key.toLowerCase();
                    const next = this.options.findIndex(
                        (o, i) =>
                            i > this.focusedIndex &&
                            o.textContent.trim().toLowerCase().startsWith(char),
                    );
                    const match =
                        next >= 0
                            ? next
                            : this.options.findIndex((o) =>
                                  o.textContent
                                      .trim()
                                      .toLowerCase()
                                      .startsWith(char),
                              );
                    if (match >= 0) {
                        this._moveFocus(match);
                    }
                }
        }
    }

    _moveFocus(index) {
        this._clearFocus();
        const option = this.options[index];
        if (!option) {
            return;
        }
        this.focusedIndex = index;
        option.classList.add('is-focused');
        // Scroll option into view within the panel
        option.scrollIntoView({ block: 'nearest' });
        // Move DOM focus to the panel so keyboard events fire there, while
        // the pseudo-focus class handles visual indication on the option.
        if (document.activeElement !== this.panel) {
            this.panel.setAttribute('tabindex', '-1');
            this.panel.focus({ preventScroll: true });
        }
    }

    _clearFocus() {
        this.options.forEach((o) => o.classList.remove('is-focused'));
        this.focusedIndex = -1;
    }

    /**
     * Flip panel to right-align when it would overflow the viewport.
     */
    _alignPanel() {
        if (this.panel.hidden) {
            return;
        }
        this.panel.classList.remove('is-right-aligned');
        const rect = this.panel.getBoundingClientRect();
        if (rect.right > window.innerWidth - 8) {
            this.panel.classList.add('is-right-aligned');
        }
    }
}

// ── Module init ───────────────────────────────────────────────────────────────

function initResourcesLandingDropdowns() {
    const wraps = document.querySelectorAll(
        `.wp-block-pulp-resources-landing ${SELECTOR_WRAP}, .archive-resources ${SELECTOR_WRAP}`,
    );

    if (!wraps.length) {
        return;
    }

    const instances = Array.from(wraps).map((wrap) => new FilterDropdown(wrap));

    // Close open dropdown when clicking outside any of them
    document.addEventListener('click', (e) => {
        instances.forEach((dd) => {
            if (dd.isOpen() && !dd.wrap.contains(e.target)) {
                dd.close();
            }
        });
    });

    // Close on focusout to the outside
    document.addEventListener('focusin', (e) => {
        instances.forEach((dd) => {
            if (dd.isOpen() && !dd.wrap.contains(e.target)) {
                dd.close();
            }
        });
    });
}

initResourcesLandingDropdowns();
